import Delegate from 'ftdomdelegate';

/**
 * <cos-contact-fab> — persistent floating contact button (WhatsApp / phone / Messenger).
 * Expandable stack. Initial position is pure CSS (vars + :has()); JS only manages OPEN
 * state, the greeting teaser + pulse ring (per-visit nudges; greeting X opts out for good),
 * and feeds the Shopify consent banner height into --cf-lift-consent so the button clears it.
 */

const X_ICON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

class CosContactFab extends HTMLElement {
  constructor() {
    super();
    this.delegate = new Delegate(this);
    this.stack = this.querySelector('[data-fab-stack]');
    this.iconEl = this.querySelector('[data-fab-icon]');
    this.toggleBtn = this.querySelector('[data-action="toggle-fab"]');
    this.teaser = this.querySelector('[data-fab-teaser]');
    this.teaserDelay = parseInt(this.getAttribute('data-teaser-delay'), 10) || 1400;
    this.pulseDelay = this.teaserDelay; // no pulse at load; arm it on the teaser cadence
    // Permanent opt-out (greeting X) lives in localStorage; per-visit suppression lives in
    // sessionStorage so both nudges return on a fresh visit (recall) without spamming pages.
    this.dismissKey = this.getAttribute('data-dismiss-key') || 'smile:fab:teaser'; // localStorage, forever
    this.seenKey = 'smile:fab:teaser:seen'; // sessionStorage — greeting already shown this visit
    this.engagedKey = 'smile:fab:engaged';  // sessionStorage — pulse already silenced this visit
    this._waIcon = this.iconEl ? this.iconEl.innerHTML : '';
    this._onDocPointer = this._onDocPointer.bind(this);
    this._onKey = this._onKey.bind(this);
  }

  connectedCallback() {
    this._restoreSession();
    this.delegate.on('click', '[data-action="toggle-fab"]', (e) => { e.preventDefault(); this.toggle(); });
    this.delegate.on('click', '[data-action="dismiss-teaser"]', (e) => { e.preventDefault(); e.stopPropagation(); this.dismissTeaser(); });
    // Tapping any channel (a stack item, or the single-mode main button) counts as engagement.
    this.delegate.on('click', '[data-channel]', () => this.markEngaged());
    document.addEventListener('click', this._onDocPointer);
    document.addEventListener('keydown', this._onKey);
    this._scheduleTeaser();
    this._schedulePulse();
    this._observeConsentBanner();
  }

  disconnectedCallback() {
    this.delegate.off();
    document.removeEventListener('click', this._onDocPointer);
    document.removeEventListener('keydown', this._onKey);
    clearTimeout(this._teaserTimer);
    clearTimeout(this._pulseTimer);
    if (this._resizeObs) this._resizeObs.disconnect();
    if (this._removalObs) this._removalObs.disconnect();
    if (this._injectObs) this._injectObs.disconnect();
    if (this._measureConsent) window.removeEventListener('resize', this._measureConsent);
  }

  get isOpen() { return this.classList.contains('is-open'); }

  toggle() { this.isOpen ? this.close() : this.open(); }

  open() {
    this.classList.add('is-open');
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'true');
    if (this.iconEl) this.iconEl.innerHTML = X_ICON;
    this._hideTeaser();
    this._markTeaserSeen();
    this.markEngaged();
  }

  close() {
    this.classList.remove('is-open');
    if (this.toggleBtn) this.toggleBtn.setAttribute('aria-expanded', 'false');
    if (this.iconEl) this.iconEl.innerHTML = this._waIcon;
  }

  _onDocPointer(e) {
    if (this.isOpen && !this.contains(e.target)) this.close();
  }

  _onKey(e) {
    if (e.key === 'Escape' && this.isOpen) this.close();
  }

  /* Greeting teaser — once per visit. Re-appears on a fresh visit (sessionStorage cleared),
     unless the visitor explicitly closed it with X (localStorage = permanent opt-out). */
  _scheduleTeaser() {
    if (!this.teaser) return;
    if (this._readStore('local', this.dismissKey)) return; // X'd before → never again
    if (this._readStore('session', this.seenKey)) return;  // already shown this visit
    this._teaserTimer = setTimeout(() => {
      if (this.isOpen) return;
      this.teaser.hidden = false;
      this._markTeaserSeen();
    }, this.teaserDelay);
  }

  _hideTeaser() { if (this.teaser) this.teaser.hidden = true; }
  _markTeaserSeen() { this._writeStore('session', this.seenKey); }

  dismissTeaser() {
    this._hideTeaser();
    this._writeStore('local', this.dismissKey); // explicit X → never show the greeting again
    this._markTeaserSeen();
    this.markEngaged();
  }

  /* Pulse ring is a per-visit nudge. The first engagement this visit (open stack, tap a
     channel, or close the greeting) silences it; a fresh visit re-arms it (sessionStorage). */
  markEngaged() {
    if (this._engaged) return;
    this._engaged = true;
    this.classList.remove('is-pulsing');
    clearTimeout(this._pulseTimer);
    this._writeStore('session', this.engagedKey);
  }

  _restoreSession() {
    if (this._readStore('session', this.engagedKey)) this._engaged = true;
    // Legacy: an earlier build wrote the engaged flag to localStorage (permanent). Clear it so
    // the pulse follows the new per-visit rule instead of staying off forever.
    this._clearStore('local', this.engagedKey);
  }

  /* Storage helpers — namespaced, fail-safe (private mode / disabled storage → no-op). */
  _store(kind) { return kind === 'session' ? window.sessionStorage : window.localStorage; }
  _readStore(kind, key) { try { return this._store(kind).getItem(key) === '1'; } catch (e) { return false; } }
  _writeStore(kind, key) { try { this._store(kind).setItem(key, '1'); } catch (e) {} }
  _clearStore(kind, key) { try { this._store(kind).removeItem(key); } catch (e) {} }

  /* The pulse must NOT animate at page load. Arm it after a short delay so it appears only
     once the page has settled — and skip entirely if the user already engaged. */
  _schedulePulse() {
    if (this._engaged) return;
    if (!this.querySelector('.contact-fab__main--pulse')) return;
    this._pulseTimer = setTimeout(() => {
      if (!this._engaged && !this.isOpen) this.classList.add('is-pulsing');
    }, this.pulseDelay);
  }

  /**
   * Shopify native consent banner is a full-width bottom bar of variable height.
   * Mirror its height into --cf-lift-consent so the FAB lifts above it. This is the
   * only JS that touches positioning, and it is dynamic-only (a CSS variable, not an
   * inline `bottom`); the INITIAL position stays 100% CSS (var defaults to 0px).
   */
  _observeConsentBanner() {
    const SEL = '#shopify-pc__banner';
    // NB: the banner is position:fixed, so offsetParent is always null — never gate on it.
    const measure = () => {
      const b = document.querySelector(SEL);
      const h = b && b.offsetHeight > 0 ? b.offsetHeight : 0;
      this.style.setProperty('--cf-lift-consent', h ? h + 16 + 'px' : '0px');
    };
    this._measureConsent = measure;

    const attach = (banner) => {
      try {
        if (!this._resizeObs) this._resizeObs = new ResizeObserver(measure);
        this._resizeObs.observe(banner);
      } catch (e) {}
      try {
        // Banner is removed from the DOM once accepted/declined — re-measure (→ 0) then.
        if (!this._removalObs) this._removalObs = new MutationObserver(() => { if (!document.querySelector(SEL)) measure(); });
        if (banner.parentNode) this._removalObs.observe(banner.parentNode, { childList: true });
      } catch (e) {}
      measure();
    };

    const existing = document.querySelector(SEL);
    if (existing) {
      attach(existing);
    } else {
      // Injected late by Shopify — watch until it appears, then stop the broad observer.
      try {
        this._injectObs = new MutationObserver(() => {
          const b = document.querySelector(SEL);
          if (b) { this._injectObs.disconnect(); attach(b); }
        });
        this._injectObs.observe(document.body, { childList: true, subtree: true });
      } catch (e) {}
    }
    measure();
    window.addEventListener('resize', measure);
  }
}

customElements.define('cos-contact-fab', CosContactFab);
